import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { productsAPI } from '@/utils/api';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ContainerStep = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finishedProductId, setFinishedProductId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const prodRes = await productsAPI.getOne(productId);
        if (!mounted) return;
        setProduct(prodRes.data);

        const contRes = await axios.get(`${API_BASE}/api/products/${productId}/recipe-containers`);
        if (!mounted) return;
        setContainers(contRes.data || []);

        // Fetch linked finished_product id for the "Go to recipe" CTA
        try {
          const fpRes = await axios.get(`${API_BASE}/api/finished-products/by-dip/${productId}`);
          if (mounted) setFinishedProductId(fpRes.data?.id || null);
        } catch (_) {
          // Endpoint may not exist; fallback - leave null
        }
      } catch (e) {
        toast.error('Erreur lors du chargement des contenants');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [productId]);

  if (loading) {
    return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="container-step-page">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="container" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">
          Contenants & Conditionnement
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} — Pots, étiquettes, packaging utilisés pour ce produit
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package2 size={20} className="text-[#205F79]" />
            Articles de conditionnement
          </CardTitle>
          <CardDescription>
            Ces articles n'entrent pas dans le pourcentage de la formule cosmétique mais figureront dans la section A.5 du DIP.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {containers.length === 0 ? (
            <div className="text-center py-12" data-testid="container-empty-state">
              <AlertCircle size={40} className="text-amber-500 mx-auto mb-4" />
              <p className="text-base font-medium text-foreground mb-2">
                Aucun contenant rattaché à ce produit
              </p>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Pour ajouter un pot, une étiquette ou un emballage, ouvrez la recette Production
                et insérez une matière de catégorie "Contenant" ou "Packaging".
              </p>
              {finishedProductId && (
                <Button
                  variant="outline"
                  className="gap-2"
                  data-testid="btn-go-to-recipe"
                  onClick={() => navigate(`/production/product/${finishedProductId}/recipe`)}
                >
                  <ExternalLink size={16} />
                  Ouvrir la recette Production
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="py-3 pr-4">Article</th>
                    <th className="py-3 pr-4">Catégorie</th>
                    <th className="py-3 pr-4">Quantité</th>
                    <th className="py-3">Fournisseur</th>
                  </tr>
                </thead>
                <tbody>
                  {containers.map((c) => (
                    <tr key={c.composition_id || c.material_id} className="border-b border-border last:border-0" data-testid={`container-row-${c.material_id}`}>
                      <td className="py-3 pr-4 font-medium">{c.material_name}</td>
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-xs font-medium">
                          {c.category}
                        </span>
                      </td>
                      <td className="py-3 pr-4 tabular-nums">
                        {Number(c.quantity).toFixed(2)} {c.unit}
                      </td>
                      <td className="py-3 text-muted-foreground">{c.supplier || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {finishedProductId && (
                <div className="mt-6 flex justify-end">
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="btn-edit-containers"
                    onClick={() => navigate(`/production/product/${finishedProductId}/recipe`)}
                  >
                    <ExternalLink size={16} />
                    Modifier dans Production
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <DipStepFooter productId={productId} currentStep="container" />
    </div>
  );
};

export default ContainerStep;
